import React, { useState, useEffect, useRef } from "react";
import { Tabs, Tab } from "react-bootstrap";
import deleteIcon from "../../../../../assets/integrations/delete-icon.svg";
import MappingFilters from "./MappingFilters";
import "./MappingProfile.css";

const MappingProfile = ({
  mappingProfiles,
  activeProfile,
  setActiveProfile,
  mappingNames,
  onAddProfile,
  onDuplicateProfile,
  onDeleteProfile,
  onSaveProfileName,
  onMakeDefault,
  onSaveAll,
  // Filters
  statusFilter,
  setStatusFilter,
  mappingFilter,
  setMappingFilter,
  isEmptyMapping,
}) => {
  // Find current profile to check if it's default
  const currentProfile = mappingProfiles.find((p) => p.id === activeProfile);
  const isDefaultProfile = currentProfile?.isDefault || false;

  // Local state for editing field name
  const [editingName, setEditingName] = useState("");
  const [isEditingName, setIsEditingName] = useState(false);

  // State for managing visible and overflow tabs
  const [visibleTabs, setVisibleTabs] = useState([]);
  const [overflowTabs, setOverflowTabs] = useState([]);
  const tabsContainerRef = useRef(null);
  const [maxVisibleTabs, setMaxVisibleTabs] = useState(10);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  // Sync editingName when activeProfile changes
  useEffect(() => {
    setEditingName(mappingNames[activeProfile] || "");
  }, [activeProfile, mappingNames]);

  // Calculate visible and overflow tabs based on container width
  useEffect(() => {
    const calculateVisibleTabs = () => {
      if (!tabsContainerRef.current) return;

      const containerWidth = tabsContainerRef.current.offsetWidth;

      // Reserve space for: add button (48px) + dropdown button (120px) + gaps (110px)
      const reservedSpace = 278
      const availableWidth = containerWidth - reservedSpace;

      // Use max-width from CSS: 200px per tab
      const maxTabWidth = 200;
      const maxTabs = Math.max(1, Math.floor(availableWidth / maxTabWidth));

      setMaxVisibleTabs(maxTabs);
    };

    // Initial calculation with a slight delay to ensure DOM is ready
    const timer = setTimeout(calculateVisibleTabs, 100);

    window.addEventListener('resize', calculateVisibleTabs);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', calculateVisibleTabs);
    };
  }, []);

  // Split tabs into visible and overflow based on maxVisibleTabs
  useEffect(() => {
    if (mappingProfiles.length <= maxVisibleTabs) {
      setVisibleTabs(mappingProfiles);
      setOverflowTabs([]);
    } else {
      // Ensure active tab is always visible
      const activeIndex = mappingProfiles.findIndex(p => p.id === activeProfile);

      if (activeIndex < maxVisibleTabs - 1) {
        // Active tab is in the visible range
        setVisibleTabs(mappingProfiles.slice(0, maxVisibleTabs));
        setOverflowTabs(mappingProfiles.slice(maxVisibleTabs));
      } else {
        // Active tab would be in overflow, so include it in visible
        const visible = [
          ...mappingProfiles.slice(0, maxVisibleTabs - 1),
          mappingProfiles[activeIndex]
        ];
        const overflow = mappingProfiles.filter((p, idx) =>
          idx >= maxVisibleTabs - 1 && p.id !== activeProfile
        );
        setVisibleTabs(visible);
        setOverflowTabs(overflow);
      }
    }
  }, [mappingProfiles, maxVisibleTabs, activeProfile]);

  // Calculate dropdown position when it opens
  useEffect(() => {
    if (showDropdown && dropdownRef.current) {
      const button = dropdownRef.current.querySelector('.dropdown-toggle-button');
      if (button) {
        const buttonRect = button.getBoundingClientRect();
        setDropdownPosition({
          top: buttonRect.bottom + 4,
          right: window.innerWidth - buttonRect.right
        });
      }
    }
  }, [showDropdown]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const handleTabChange = (profileId) => {
    if (profileId === "add-new") {
      onAddProfile();
    } else {
      setActiveProfile(profileId);
    }
  };


  // Handle edit mode toggle
  const handleEditClick = () => {
    setEditingName(currentProfile?.name || "");
    setIsEditingName(true);
  };

  // Handle field name input change (only update local state)
  const handleFieldNameChange = (e) => {
    const newValue = e.target.value;
    if (newValue.length <= 39) {
      setEditingName(newValue);
    }
  };

  // Handle Save button in edit mode
  const handleSaveName = () => {
    const trimmedValue = editingName.trim();
    if (!trimmedValue) {
      setEditingName(currentProfile?.name || "");
      setIsEditingName(false);
      return;
    }

    // Call API to update name only
    if (onSaveProfileName) {
      onSaveProfileName(trimmedValue);
    }
    setIsEditingName(false);
  };

  // Handle Cancel button
  const handleCancelEdit = () => {
    setEditingName(currentProfile?.name || "");
    setIsEditingName(false);
  };

  const handleFieldNameKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSaveName();
    } else if (e.key === "Escape") {
      handleCancelEdit();
    }
  };


  return (
    <>
      {/* Mapping Profile Tabs Section */}
      <div className="mapping-profile-section">
        <div className="mapping-profile-header" ref={tabsContainerRef}>
          <Tabs
            activeKey={activeProfile}
            onSelect={(k) => k && handleTabChange(k)}
            className="mapping-profile-tabs"
          >
            {visibleTabs.map((profile) => (
              <Tab
                key={profile.id}
                eventKey={profile.id}
                title={
                  <span className="tab-title-wrapper">
                    <span className="tab-title-text">
                      {profile.name}
                      {profile.isDefault && <span className="default-badge-tab"> (Default)</span>}
                    </span>
                  </span>
                }
              />
            ))}
          </Tabs>

          {/* Dropdown for overflow tabs */}
          {overflowTabs.length > 0 && (
            <div className="overflow-tabs-dropdown" ref={dropdownRef}>
              <button
                className="dropdown-toggle-button"
                onClick={() => setShowDropdown(!showDropdown)}
                type="button"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ marginRight: '4px' }}
                >
                  <circle cx="8" cy="3" r="1.5" fill="currentColor" />
                  <circle cx="8" cy="8" r="1.5" fill="currentColor" />
                  <circle cx="8" cy="13" r="1.5" fill="currentColor" />
                </svg>
                More ({overflowTabs.length})
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ marginLeft: '6px' }}
                  className={showDropdown ? 'dropdown-arrow-up' : 'dropdown-arrow-down'}
                >
                  <path
                    d="M3 4.5L6 7.5L9 4.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              {showDropdown && (
                <div
                  className="dropdown-menu-custom"
                  style={{
                    top: `${dropdownPosition.top}px`,
                    right: `${dropdownPosition.right}px`
                  }}
                >
                  {overflowTabs.map((profile) => (
                    <button
                      key={profile.id}
                      className={`dropdown-item-custom ${profile.id === activeProfile ? 'active' : ''}`}
                      onClick={() => {
                        handleTabChange(profile.id);
                        setShowDropdown(false);
                      }}
                      type="button"
                    >
                      {profile.name}
                      {profile.isDefault && <span className="default-badge-dropdown"> (Default)</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <button
            className="add-new-tab-button"
            onClick={() => handleTabChange("add-new")}
            title="Add new mapping profile"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="plus-icon"
            >
              <path
                d="M7 3V11M3 7H11"
                stroke="#3b82f6"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Field Mapping Name Section */}
      <div className="mapping-section">
        <div className="filter-group">
          <div className="filter-group-left">
            <h2 className="section-title">Field Mapping Name:</h2>

            {!isEditingName ? (
              <div className="field-name-display">
                {isDefaultProfile && (
                  <span className="default-badge">Default</span>
                )}
                <span className="field-name-text">{currentProfile?.name || ""}</span>
                <button className="edit-icon-button" onClick={handleEditClick}>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7.33333 2.66667H2.66667C2.31304 2.66667 1.97391 2.80714 1.72386 3.05719C1.47381 3.30724 1.33333 3.64638 1.33333 4V13.3333C1.33333 13.687 1.47381 14.0261 1.72386 14.2761C1.97391 14.5262 2.31304 14.6667 2.66667 14.6667H12C12.3536 14.6667 12.6928 14.5262 12.9428 14.2761C13.1929 14.0261 13.3333 13.687 13.3333 13.3333V8.66667M12.3333 1.66667C12.5986 1.40145 12.9582 1.25244 13.3333 1.25244C13.7085 1.25244 14.0681 1.40145 14.3333 1.66667C14.5985 1.93189 14.7476 2.29158 14.7476 2.66667C14.7476 3.04176 14.5985 3.40145 14.3333 3.66667L8 10L5.33333 10.6667L6 8L12.3333 1.66667Z"
                      stroke="#64748b"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            ) : (
              <div className="field-name-edit-container">
                {isDefaultProfile && (
                  <span className="default-badge">Default</span>
                )}
                <input
                  type="text"
                  className="field-name-input"
                  placeholder="Mapping1"
                  value={editingName}
                  onChange={handleFieldNameChange}
                  onKeyDown={handleFieldNameKeyDown}
                  maxLength={39}
                  autoFocus
                />
                <button className="field-name-save-button" onClick={handleSaveName}>
                  Save
                </button>
                <button className="field-name-cancel-button" onClick={handleCancelEdit}>
                  Cancel
                </button>
              </div>
            )}

            {!isEmptyMapping && (
              <>
                <button
                  className="field-action-button duplicate-button"
                  onClick={onDuplicateProfile}
                >
                  <svg
                    width="11.67"
                    height="11.67"
                    viewBox="0 0 12 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M10 4H6C5.44772 4 5 4.44772 5 5V9C5 9.55228 5.44772 10 6 10H10C10.5523 10 11 9.55228 11 9V5C11 4.44772 10.5523 4 10 4Z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M3 8H2C1.73478 8 1.48043 7.89464 1.29289 7.70711C1.10536 7.51957 1 7.26522 1 7V3C1 2.73478 1.10536 2.48043 1.29289 2.29289C1.48043 2.10536 1.73478 2 2 2H6C6.26522 2 6.51957 2.10536 6.70711 2.29289C6.89464 2.48043 7 2.73478 7 3V4"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span>Duplicate</span>
                </button>

                <button
                  className={`field-action-button delete-button ${isDefaultProfile ? 'disabled' : ''}`}
                  onClick={() => {
                    if (activeProfile && typeof activeProfile === 'string') {
                      onDeleteProfile(activeProfile);
                    }
                  }}
                  disabled={isDefaultProfile || !activeProfile}
                >
                  <img
                    src={deleteIcon}
                    alt="Delete"
                    className="delete-icon"
                  />
                  <span>Delete</span>
                </button>

                {!isDefaultProfile && activeProfile && (
                  <button
                    className="field-action-button make-default-button"
                    onClick={() => {
                      if (activeProfile && typeof activeProfile === 'string') {
                        onMakeDefault(activeProfile);
                      }
                    }}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="make-default-icon"
                    >
                      <path
                        d="M7 1L8.545 5.13L13 5.635L9.635 8.455L10.635 13L7 10.635L3.365 13L4.365 8.455L1 5.635L5.455 5.13L7 1Z"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill="currentColor"
                      />
                    </svg>
                    <span>Make Default</span>
                  </button>
                )}
              </>
            )}
          </div>
          <div className="filter-group-right">
            <MappingFilters
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              mappingFilter={mappingFilter}
              setMappingFilter={setMappingFilter}
            />
          </div>
        </div>
      </div >
    </>
  );
};

export default MappingProfile;
